import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { getUser } from '../auth';

function normalizeArabic(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[أإآا]/g, 'ا')
    .replace(/[ى]/g, 'ي')
    .replace(/[ؤ]/g, 'و')
    .replace(/[ئ]/g, 'ي')
    .replace(/[ة]/g, 'ه')
    .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
    .replace(/\s+/g, ' ')
    .trim();
}

function SmartSelect({
  label,
  placeholder,
  items,
  value,
  onChange,
  getLabel,
  getSubLabel,
  required = false,
  disabled = false,
  emptyText = 'لا توجد نتائج مطابقة'
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const selectedItem = useMemo(() => {
    return items.find(item => String(item.id) === String(value)) || null;
  }, [items, value]);

  useEffect(() => {
    if (selectedItem) {
      setQuery(getLabel(selectedItem));
    }
  }, [selectedItem, getLabel]);

  const normalizedQuery = normalizeArabic(query);

  const filteredItems = useMemo(() => {
    if (!normalizedQuery) {
      return items.slice(0, 8);
    }

    return items
      .filter(item => {
        const main = normalizeArabic(getLabel(item));
        const sub = normalizeArabic(getSubLabel ? getSubLabel(item) : '');
        return main.includes(normalizedQuery) || sub.includes(normalizedQuery);
      })
      .slice(0, 10);
  }, [items, normalizedQuery, getLabel, getSubLabel]);

  const handleTyping = (e) => {
    const nextValue = e.target.value;
    setQuery(nextValue);
    setOpen(true);

    if (value && selectedItem && nextValue !== getLabel(selectedItem)) {
      onChange('');
    }
  };

  const chooseItem = (item) => {
    onChange(item.id);
    setQuery(getLabel(item));
    setOpen(false);
  };

  return (
    <div className="relative">
      <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
      <input
        type="text"
        value={query}
        onChange={handleTyping}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        required={required && !value}
        disabled={disabled}
        autoComplete="off"
        className="w-full border-2 border-gray-300 rounded-xl p-3 focus:border-orange-500 focus:outline-none disabled:bg-gray-100 mobile-input"
      />

      {open && !disabled && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-auto">
          {filteredItems.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">{emptyText}</div>
          ) : (
            filteredItems.map(item => (
              <button
                type="button"
                key={item.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chooseItem(item)}
                className={`w-full text-right p-3 hover:bg-orange-50 border-b last:border-b-0 ${String(value) === String(item.id) ? 'bg-orange-100' : ''}`}
              >
                <div className="font-bold text-gray-800">{getLabel(item)}</div>
                {getSubLabel && getSubLabel(item) && (
                  <div className="text-xs text-gray-500 mt-1">{getSubLabel(item)}</div>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function Transactions() {
  const navigate = useNavigate();
  const user = getUser();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [transactionType, setTransactionType] = useState('recharge');
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUserBalance, setCurrentUserBalance] = useState(null);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [confirmingTransferId, setConfirmingTransferId] = useState('');
  const [rejectingTransferId, setRejectingTransferId] = useState('');
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionMode, setCorrectionMode] = useState('cancel');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionAmount, setCorrectionAmount] = useState('');
  const [savingCorrection, setSavingCorrection] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [walletFilter, setWalletFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [formData, setFormData] = useState({
    store_id: '',
    wallet_id: '',
    amount: '',
    to_user_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [txRes, storesRes, walletsRes, usersRes, balanceRes, pendingRes] = await Promise.allSettled([
        apiFetch('/transactions'),
        apiFetch('/stores'),
        apiFetch('/wallets'),
        apiFetch('/users'),
        apiFetch('/user-balances/me'),
        apiFetch('/transfers/pending')
      ]);

      if (txRes.status === 'fulfilled' && txRes.value?.ok) {
        const txData = await txRes.value.json();
        setTransactions(Array.isArray(txData) ? txData : (txData.transactions || []));
      }

      if (storesRes.status === 'fulfilled' && storesRes.value?.ok) {
        const storesData = await storesRes.value.json();
        const storesList = Array.isArray(storesData) ? storesData : (storesData.stores || []);
        setStores(storesList.filter(s => s.status !== 'deleted'));
      }

      if (walletsRes.status === 'fulfilled' && walletsRes.value?.ok) {
        const walletsData = await walletsRes.value.json();
        const walletsList = Array.isArray(walletsData) ? walletsData : (walletsData.wallets || []);
        setWallets(walletsList.filter(w => w.status !== 'deleted'));
      }

      if (usersRes.status === 'fulfilled' && usersRes.value?.ok) {
        const usersData = await usersRes.value.json();
        const usersList = Array.isArray(usersData) ? usersData : (usersData.users || []);
        setUsers(usersList);
      }

      if (balanceRes.status === 'fulfilled' && balanceRes.value?.ok) {
        const balanceData = await balanceRes.value.json();
        const balanceValue = balanceData.balance ?? balanceData.user_balance?.balance ?? balanceData.data?.balance;
        if (balanceValue !== undefined && balanceValue !== null) {
          setCurrentUserBalance(Number(balanceValue));
        }
      }

      if (pendingRes.status === 'fulfilled' && pendingRes.value?.ok) {
        const pendingData = await pendingRes.value.json();
        setPendingTransfers(pendingData.transfers || []);
      }
    } catch (err) {
      setError('خطأ في جلب البيانات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ store_id: '', wallet_id: '', amount: '', to_user_id: '', description: '' });
  };

  const changeType = (type) => {
    setTransactionType(type);
    resetForm();
    setError(null);
    setSuccessMessage('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const setFieldValue = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);
    return number.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const selectedStore = stores.find(store => String(store.id) === String(formData.store_id));
  const selectedWallet = wallets.find(wallet => String(wallet.id) === String(formData.wallet_id));
  const regularWallets = wallets.filter(wallet => String(wallet.id) !== 'wallet-masrafi' && String(wallet.type) !== 'masrafi');
  const selectedUser = users.find(u => String(u.id) === String(formData.to_user_id));

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄', collection: '💵', transfer: '↔️', expense: '💸', company_settlement: '🏢', manual_balance_topup: '➕', masrafi: '🏦'
    };
    return icons[type] || '💳';
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن', collection: 'قبض', transfer: 'تحويل', expense: 'مصروف', company_settlement: 'تسديد الشركة', manual_balance_topup: 'إضافة رصيد', masrafi: 'مصرفي'
    };
    return labels[type] || type || '-';
  };

  const getStatusLabel = (status) => {
    const labels = { completed: 'مكتملة', confirmed: 'مؤكدة', pending: 'معلقة', rejected: 'مرفوضة', cancelled: 'ملغية' };
    return labels[status] || status || '-';
  };

  const isCancelled = (tx) => String(tx?.status || '').toLowerCase() === 'cancelled';
  const canCorrect = user && ['owner', 'manager'].includes(String(user.role || ''));

  const typeDescriptions = {
    recharge: 'ينقص من المحفظة المختارة ويزيد رصيد/مديونية المحل.',
    collection: 'ينقص مديونية المحل ويزيد رصيد المستخدم الذي قبض.',
    transfer: 'يرسل تحويل معلق إلى مستخدم آخر حتى يؤكد الاستلام.',
    expense: 'يخصم من رصيد المستخدم الحالي، وليس من أي محفظة.',
    company_settlement: 'تسديد للشركة أو دعم محفظة حسب نوع العملية المعتمد.'
  };

  const getConfirmText = () => {
    const amount = formatNumber(formData.amount);
    switch (transactionType) {
      case 'recharge': return `تأكيد شحن ${amount} للمحل "${selectedStore?.name || ''}" من "${selectedWallet?.name || ''}"؟`;
      case 'collection': return `تأكيد قبض ${amount} من المحل "${selectedStore?.name || ''}"؟ سيزيد رصيد المستخدم الحالي.`;
      case 'transfer': return `تأكيد إرسال تحويل بقيمة ${amount} إلى "${selectedUser?.name || ''}"؟ العملية ستبقى معلقة حتى يؤكد المستلم.`;
      case 'expense': return `تأكيد تسجيل مصروف بقيمة ${amount}؟ سيتم الخصم من رصيد المستخدم الحالي وليس من المحفظة.`;
      case 'company_settlement': return `تأكيد تسديد الشركة بقيمة ${amount} إلى "${selectedWallet?.name || ''}"؟ سيتم خصم نفس المبلغ من رصيد المستخدم. ملاحظة: مصرفي له مسار خاص عبر طلبات مصرفي.`;
      default: return 'تأكيد تنفيذ العملية؟';
    }
  };

  const validateForm = () => {
    const amount = Number(formData.amount);
    if (!Number.isFinite(amount) || amount <= 0) return 'المبلغ يجب أن يكون أكبر من صفر';
    if ((transactionType === 'recharge' || transactionType === 'collection') && !formData.store_id) return 'اختر المحل أولاً';
    if ((transactionType === 'recharge' || transactionType === 'company_settlement') && !formData.wallet_id) return 'اختر المحفظة أولاً';
    if ((transactionType === 'recharge' || transactionType === 'company_settlement') && selectedWallet && (String(selectedWallet.id) === 'wallet-masrafi' || String(selectedWallet.type) === 'masrafi')) {
      return 'محفظة مصرفي لها مسار خاص عبر طلبات مصرفي، ولا تظهر في الشحن أو تسديد الشركة العادي.';
    }
    if (transactionType === 'transfer' && !formData.to_user_id) return 'اختر المستخدم المستقبل أولاً';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    if (!window.confirm(getConfirmText())) return;

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');
      let endpoint = '';
      let data = {};
      const amount = Number(formData.amount);

      switch (transactionType) {
        case 'recharge': endpoint = '/transactions/recharge'; data = { store_id: formData.store_id, wallet_id: formData.wallet_id, amount, description: formData.description }; break;
        case 'collection': endpoint = '/transactions/collection'; data = { store_id: formData.store_id, amount, description: formData.description }; break;
        case 'transfer': endpoint = '/transactions/transfer'; data = { to_user_id: formData.to_user_id, amount, description: formData.description }; break;
        case 'expense': endpoint = '/transactions/expense'; data = { amount, description: formData.description }; break;
        case 'company_settlement': endpoint = '/transactions/company-settlement'; data = { wallet_id: formData.wallet_id, amount, description: formData.description }; break;
        default: return;
      }

      const response = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(data) });
      const result = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) { setError(result.message || 'حدث خطأ أثناء حفظ العملية'); return; }
      resetForm();
      setShowForm(false);
      setSuccessMessage(result.message || 'تم حفظ العملية بنجاح');
      await fetchData();
    } catch (err) {
      console.error('Error:', err);
      setError('حدث خطأ أثناء حفظ العملية');
    } finally {
      setSaving(false);
    }
  };

  const confirmTransfer = async (transfer) => {
    if (!window.confirm(`تأكيد استلام تحويل بقيمة ${formatNumber(transfer.amount)} من ${transfer.from_user_name || 'مستخدم'}؟`)) return;
    try {
      setConfirmingTransferId(transfer.id);
      setError(null);
      setSuccessMessage('');
      const response = await apiFetch(`/transfers/${transfer.id}/confirm`, { method: 'POST' });
      const result = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) { setError(result.message || 'فشل تأكيد التحويل'); return; }
      setSuccessMessage(result.message || 'تم تأكيد التحويل بنجاح');
      await fetchData();
    } catch (err) { console.error(err); setError('حدث خطأ أثناء تأكيد التحويل'); }
    finally { setConfirmingTransferId(''); }
  };

  const rejectTransfer = async (transfer) => {
    if (!window.confirm(`رفض تحويل بقيمة ${formatNumber(transfer.amount)} من ${transfer.from_user_name || 'مستخدم'}؟`)) return;
    try {
      setRejectingTransferId(transfer.id);
      setError(null);
      setSuccessMessage('');
      const response = await apiFetch(`/transfers/${transfer.id}/reject`, { method: 'POST' });
      const result = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) { setError(result.message || 'فشل رفض التحويل'); return; }
      setSuccessMessage(result.message || 'تم رفض التحويل بنجاح');
      await fetchData();
    } catch (err) { console.error(err); setError('حدث خطأ أثناء رفض التحويل'); }
    finally { setRejectingTransferId(''); }
  };

  const openCorrection = (tx, mode) => {
    setCorrectionTarget(tx);
    setCorrectionMode(mode);
    setCorrectionReason('');
    setCorrectionAmount(mode === 'correct' ? String(tx.amount || '') : '');
    setError(null);
    setSuccessMessage('');
  };

  const closeCorrection = () => {
    if (savingCorrection) return;
    setCorrectionTarget(null);
    setCorrectionMode('cancel');
    setCorrectionReason('');
    setCorrectionAmount('');
  };

  const submitCorrection = async () => {
    if (!correctionTarget || savingCorrection) return;
    const reason = correctionReason.trim();
    if (!reason) { setError('اكتب سبب التصحيح أو الإلغاء أولاً'); return; }

    const isCorrectMode = correctionMode === 'correct';
    const newAmount = Number(correctionAmount);
    if (isCorrectMode && (!Number.isFinite(newAmount) || newAmount <= 0)) {
      setError('اكتب المبلغ الصحيح أكبر من صفر');
      return;
    }

    const confirmText = isCorrectMode
      ? `تأكيد تعديل مبلغ العملية من ${formatNumber(correctionTarget.amount)} إلى ${formatNumber(newAmount)}؟ سيتم تعديل الأرصدة تلقائياً.`
      : `تأكيد إلغاء العملية رقم ${correctionTarget.reference_number || correctionTarget.id}؟ سيتم عكس أثرها على الأرصدة.`;
    if (!window.confirm(confirmText)) return;

    try {
      setSavingCorrection(true);
      setError(null);
      setSuccessMessage('');
      const endpoint = isCorrectMode
        ? `/transactions/${correctionTarget.id}/correct-amount`
        : `/transactions/${correctionTarget.id}/cancel`;
      const payload = isCorrectMode ? { amount: newAmount, reason } : { reason };
      const response = await apiFetch(endpoint, { method: 'POST', body: JSON.stringify(payload) });
      const result = response ? await response.json().catch(() => ({})) : {};
      if (!response?.ok) { setError(result.message || 'فشل تنفيذ التصحيح'); return; }
      setSuccessMessage(result.message || 'تم تنفيذ التصحيح بنجاح');
      closeCorrection();
      await fetchData();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تنفيذ التصحيح');
    } finally {
      setSavingCorrection(false);
    }
  };

  const getTransactionSearchText = (tx) => normalizeArabic([
    tx.id, tx.type, getTypeLabel(tx.type), tx.amount, tx.description, tx.store_name, tx.wallet_name, tx.user_name, tx.to_user_name, tx.status_label, tx.created_at
  ].filter(Boolean).join(' '));

  const filteredTransactions = useMemo(() => {
    const q = normalizeArabic(searchText);
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    return transactions.filter((tx) => {
      if (typeFilter !== 'all' && String(tx.type) !== String(typeFilter)) return false;
      if (storeFilter !== 'all' && String(tx.store_id || '') !== String(storeFilter)) return false;
      if (walletFilter !== 'all' && String(tx.wallet_id || '') !== String(walletFilter)) return false;
      if (q && !getTransactionSearchText(tx).includes(q)) return false;

      const txTime = tx.created_at ? new Date(tx.created_at).getTime() : null;
      if (fromTime && Number.isFinite(txTime) && txTime < fromTime) return false;
      if (toTime && Number.isFinite(txTime) && txTime > toTime) return false;
      return true;
    });
  }, [transactions, searchText, typeFilter, storeFilter, walletFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchText('');
    setTypeFilter('all');
    setStoreFilter('all');
    setWalletFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const printReceipt = (tx) => {
    const typeLabel = getTypeLabel(tx.type);
    const receiptHtml = `
      <!doctype html>
      <html lang="ar" dir="rtl">
      <head>
        <meta charset="utf-8" />
        <title>إيصال عملية</title>
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 24px; font-family: Tahoma, Arial, sans-serif; background: #f3f4f6; color: #111827; }
          .receipt { max-width: 760px; margin: 0 auto; background: white; border: 2px solid #111827; border-radius: 24px; padding: 28px; }
          .head { text-align: center; border-bottom: 2px dashed #d1d5db; padding-bottom: 18px; margin-bottom: 22px; }
          h1 { margin: 0 0 8px; font-size: 28px; }
          .subtitle { color: #6b7280; font-weight: 700; }
          .amount { text-align: center; background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412; border-radius: 20px; padding: 18px; margin: 18px 0; font-size: 30px; font-weight: 900; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
          .box { border: 1px solid #e5e7eb; border-radius: 16px; padding: 12px; min-height: 66px; }
          .label { color: #6b7280; font-size: 13px; font-weight: 800; margin-bottom: 5px; }
          .value { font-size: 16px; font-weight: 900; word-break: break-word; }
          .full { grid-column: 1 / -1; }
          .foot { margin-top: 26px; display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
          .sign { border-top: 1px solid #9ca3af; padding-top: 10px; text-align: center; color: #374151; font-weight: 800; }
          @media print { body { background: white; padding: 0; } .receipt { border-radius: 0; border: 0; max-width: none; } .no-print { display: none; } }
          @media (max-width: 640px) { body { padding: 10px; } .grid, .foot { grid-template-columns: 1fr; } }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="head">
            <h1>نظام الإشارة الإداري</h1>
            <div class="subtitle">إيصال عملية مالية</div>
          </div>
          <div class="amount">${formatNumber(tx.amount)} د.ل</div>
          <div class="grid">
            <div class="box"><div class="label">نوع العملية</div><div class="value">${getTypeIcon(tx.type)} ${typeLabel}</div></div>
            <div class="box"><div class="label">التاريخ</div><div class="value">${formatDate(tx.created_at)}</div></div>
            <div class="box"><div class="label">المحل</div><div class="value">${tx.store_name || '-'}</div></div>
            <div class="box"><div class="label">المحفظة</div><div class="value">${tx.wallet_name || '-'}</div></div>
            <div class="box"><div class="label">المستخدم</div><div class="value">${tx.user_name || user?.name || '-'}</div></div>
            <div class="box"><div class="label">إلى مستخدم</div><div class="value">${tx.to_user_name || '-'}</div></div>
            <div class="box full"><div class="label">الوصف</div><div class="value">${tx.description || '-'}</div></div>
            <div class="box full"><div class="label">رقم العملية</div><div class="value">${tx.id || '-'}</div></div>
          </div>
          <div class="foot">
            <div class="sign">توقيع المستلم</div>
            <div class="sign">توقيع المسلم</div>
          </div>
        </div>
        <script>window.onload = function(){ window.print(); };</script>
      </body>
      </html>
    `;

    const win = window.open('', '_blank', 'width=900,height=760');
    if (!win) {
      alert('المتصفح منع فتح نافذة الطباعة. اسمح بالنوافذ المنبثقة لهذا الموقع.');
      return;
    }
    win.document.open();
    win.document.write(receiptHtml);
    win.document.close();
  };

  const CorrectionButtons = ({ tx }) => {
    const disabled = !canCorrect || isCancelled(tx) || ['transfer', 'transfer_in', 'transfer_out'].includes(String(tx.type));
    if (!canCorrect) return null;
    return (
      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => openCorrection(tx, 'correct')}
          disabled={disabled}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 text-white font-bold px-3 py-2 rounded-xl text-xs"
        >
          ✏️ تعديل المبلغ
        </button>
        <button
          type="button"
          onClick={() => openCorrection(tx, 'cancel')}
          disabled={disabled}
          className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:text-gray-500 text-white font-bold px-3 py-2 rounded-xl text-xs"
        >
          🛑 إلغاء
        </button>
      </div>
    );
  };

  const TransactionRow = ({ tx }) => (
    <tr className={`border-b hover:bg-gray-50 ${isCancelled(tx) ? 'bg-red-50 opacity-75' : ''}`}>
      <td className="px-5 py-4">
        <span className="inline-block bg-orange-100 text-orange-900 px-3 py-1 rounded-full text-xs font-bold">{getTypeIcon(tx.type)} {getTypeLabel(tx.type)}</span>
        <div className={`mt-2 text-xs font-bold ${isCancelled(tx) ? 'text-red-700' : 'text-gray-500'}`}>{getStatusLabel(tx.status)}</div>
      </td>
      <td className={`px-5 py-4 font-bold ${isCancelled(tx) ? 'text-red-700 line-through' : 'text-green-700'}`}>{formatNumber(tx.amount)}</td>
      <td className="px-5 py-4 text-gray-800">
        <div className="font-bold">{tx.description || '-'}</div>
        <div className="text-xs text-gray-500 mt-1">{[tx.store_name, tx.wallet_name, tx.user_name].filter(Boolean).join(' — ')}</div>
      </td>
      <td className="px-5 py-4 text-xs text-gray-600">{formatDate(tx.created_at)}</td>
      <td className="px-5 py-4">
        <div className="flex flex-col gap-2">
          <button onClick={() => printReceipt(tx)} className="bg-gray-900 hover:bg-black text-white font-bold px-4 py-2 rounded-xl text-sm">🧾 إيصال</button>
          <CorrectionButtons tx={tx} />
        </div>
      </td>
    </tr>
  );

  const TransactionCard = ({ tx }) => (
    <div className={`mobile-card bg-white rounded-2xl shadow p-4 border ${isCancelled(tx) ? 'border-red-200 bg-red-50 opacity-80' : 'border-gray-100'}`}>
      <div className="flex justify-between items-start gap-3 mb-3">
        <div>
          <span className="bg-orange-100 text-orange-900 px-3 py-1 rounded-full text-xs font-bold">{getTypeIcon(tx.type)} {getTypeLabel(tx.type)}</span>
          <div className={`mt-2 text-xs font-bold ${isCancelled(tx) ? 'text-red-700' : 'text-gray-500'}`}>{getStatusLabel(tx.status)}</div>
        </div>
        <span className={`font-black text-lg ${isCancelled(tx) ? 'text-red-700 line-through' : 'text-green-700'}`}>{formatNumber(tx.amount)}</span>
      </div>
      <div className="text-gray-900 font-bold mb-2">{tx.description || '-'}</div>
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
        <div><b>المحل:</b> {tx.store_name || '-'}</div>
        <div><b>المحفظة:</b> {tx.wallet_name || '-'}</div>
        <div><b>المستخدم:</b> {tx.user_name || '-'}</div>
        <div><b>التاريخ:</b> {formatDate(tx.created_at)}</div>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <button onClick={() => printReceipt(tx)} className="w-full bg-gray-900 hover:bg-black text-white font-bold py-3 rounded-xl">🧾 طباعة إيصال</button>
        <CorrectionButtons tx={tx} />
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen" dir="rtl"><div className="text-xl font-bold">جاري التحميل...</div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8 mobile-page" dir="rtl">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6 mobile-header-stack">
        <div>
          <button onClick={() => navigate('/')} className="text-orange-600 hover:text-orange-800 mb-3 font-bold">← رجوع</button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mobile-title">💳 العمليات المالية</h1>
          <p className="text-gray-600 mt-2">نظام الإشارة الإداري — تسجيل الشحن، القبض، التحويل، المصروف والتسديد</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mobile-actions">
          <button onClick={() => navigate('/user-balance')} className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-6 rounded-xl shadow">💼 رصيدي وعملياتي</button>
          <button onClick={() => { setShowForm(!showForm); setError(null); setSuccessMessage(''); }} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 px-6 rounded-xl shadow">{showForm ? 'إغلاق النموذج' : '➕ عملية جديدة'}</button>
        </div>
      </div>

      {successMessage && <div className="bg-green-100 border border-green-300 text-green-800 rounded-xl p-4 mb-4 font-bold">{successMessage}</div>}
      {error && <div className="bg-red-100 border border-red-300 text-red-800 rounded-xl p-4 mb-4 font-bold">{error}</div>}

      {pendingTransfers.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 md:p-6 mb-6">
          <h2 className="text-2xl font-bold text-yellow-900 mb-2">⚠️ تحويلات تنتظر تأكيدك</h2>
          <p className="text-sm text-yellow-800 mb-4">هذه التحويلات مرسلة لك، ولا تنتقل الأرصدة فعلياً إلا بعد الضغط على تأكيد الاستلام.</p>
          <div className="space-y-3">
            {pendingTransfers.map((transfer) => (
              <div key={transfer.id} className="bg-white rounded-2xl border border-yellow-100 p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <p className="font-bold text-gray-900">تحويل من: {transfer.from_user_name || '-'}</p>
                  <p className="text-sm text-gray-600 mt-1">المبلغ: <b>{formatNumber(transfer.amount)}</b> — {transfer.description || 'بدون وصف'}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(transfer.created_at)}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mobile-actions">
                  <button onClick={() => confirmTransfer(transfer)} disabled={confirmingTransferId === transfer.id || rejectingTransferId === transfer.id} className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white font-bold px-5 py-3 rounded-xl">{confirmingTransferId === transfer.id ? 'جاري التأكيد...' : '✅ تأكيد الاستلام'}</button>
                  <button onClick={() => rejectTransfer(transfer)} disabled={confirmingTransferId === transfer.id || rejectingTransferId === transfer.id} className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-bold px-5 py-3 rounded-xl">{rejectingTransferId === transfer.id ? 'جاري الرفض...' : '❌ رفض التحويل'}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-8 border border-gray-100 mobile-card">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">إضافة عملية جديدة</h2>
              <p className="text-gray-600 mt-1">{typeDescriptions[transactionType]}</p>
            </div>
            <div className="bg-gray-100 rounded-xl p-3 text-sm text-gray-700">
              <div><strong>المستخدم الحالي:</strong> {user?.name || '-'}</div>
              <div><strong>رصيد المستخدم:</strong> {currentUserBalance === null ? 'غير متاح حالياً' : formatNumber(currentUserBalance)}</div>
            </div>
          </div>

          <div className="mb-5 grid grid-cols-2 md:grid-cols-5 gap-2 mobile-grid-buttons">
            {['recharge', 'collection', 'transfer', 'expense', 'company_settlement'].map(type => (
              <button type="button" key={type} onClick={() => changeType(type)} disabled={saving} className={`py-3 px-3 rounded-xl font-bold transition text-sm disabled:opacity-60 ${transactionType === type ? 'bg-gray-900 text-white shadow' : 'bg-gray-200 text-gray-800 hover:bg-orange-100'}`}>{getTypeIcon(type)} {getTypeLabel(type)}</button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mobile-form-grid">
            {(transactionType === 'recharge' || transactionType === 'collection') && (
              <SmartSelect label="المحل" placeholder="اكتب اسم المحل أو رقم الهاتف..." items={stores} value={formData.store_id} onChange={(value) => setFieldValue('store_id', value)} getLabel={(store) => store.name} getSubLabel={(store) => [store.phone, store.address].filter(Boolean).join(' — ')} required disabled={saving} />
            )}
            {(transactionType === 'recharge' || transactionType === 'company_settlement') && (
              <SmartSelect label="المحفظة" placeholder="اكتب اسم المحفظة..." items={regularWallets} value={formData.wallet_id} onChange={(value) => setFieldValue('wallet_id', value)} getLabel={(wallet) => wallet.name} getSubLabel={(wallet) => `الرصيد الحالي: ${formatNumber(wallet.balance)}`} required disabled={saving} />
            )}
            {transactionType === 'transfer' && (
              <SmartSelect label="المستخدم المستقبل" placeholder="اكتب اسم المدير أو المستخدم..." items={users.filter(u => String(u.id) !== String(user?.id))} value={formData.to_user_id} onChange={(value) => setFieldValue('to_user_id', value)} getLabel={(u) => u.name} getSubLabel={(u) => u.role === 'owner' ? 'مالك' : 'مدير'} required disabled={saving} />
            )}
            {transactionType === 'expense' && (
              <div className="md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-yellow-900"><div className="font-bold mb-1">تنبيه المصروف</div><div>المصروف سيُخصم من رصيد المستخدم الحالي: <strong>{user?.name || '-'}</strong></div><div className="text-sm mt-1">لا يتم اختيار محفظة في المصروف حسب منطق العمل الجديد.</div></div>
            )}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">المبلغ</label>
              <input type="number" name="amount" placeholder="0" value={formData.amount} onChange={handleInputChange} required min="0.01" step="0.01" disabled={saving} className="w-full border-2 border-gray-300 rounded-xl p-3 focus:border-orange-500 focus:outline-none disabled:bg-gray-100 mobile-input" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">الوصف</label>
              <input type="text" name="description" placeholder="الوصف أو الملاحظة (اختياري)" value={formData.description} onChange={handleInputChange} disabled={saving} className="w-full border-2 border-gray-300 rounded-xl p-3 focus:border-orange-500 focus:outline-none disabled:bg-gray-100 mobile-input" />
            </div>
            <button type="submit" disabled={saving} className="bg-gray-900 hover:bg-black text-white font-bold py-3 px-6 rounded-xl md:col-span-2 disabled:opacity-60 disabled:cursor-not-allowed mobile-primary-button">{saving ? 'جاري الحفظ...' : '✅ حفظ العملية'}</button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-4 md:p-5 mb-6 border border-gray-100 mobile-card">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3 justify-between">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 flex-1 mobile-form-grid">
            <div className="xl:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">بحث</label>
              <input value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="ابحث بالوصف، المحل، المحفظة، المستخدم..." className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">النوع</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input">
                <option value="all">كل العمليات</option>
                <option value="recharge">شحن</option>
                <option value="collection">قبض</option>
                <option value="expense">مصروف</option>
                <option value="company_settlement">تسديد الشركة</option>
                <option value="manual_balance_topup">إضافة رصيد</option>
                <option value="transfer">تحويل</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">المحل</label>
              <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input">
                <option value="all">كل المحلات</option>
                {stores.map(store => <option key={store.id} value={store.id}>{store.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">المحفظة</label>
              <select value={walletFilter} onChange={(e) => setWalletFilter(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input">
                <option value="all">كل المحافظ</option>
                {wallets.map(wallet => <option key={wallet.id} value={wallet.id}>{wallet.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">من تاريخ</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input" />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">إلى تاريخ</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border-2 border-gray-200 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input" />
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[170px] mobile-actions">
            <button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl">تحديث</button>
            <button onClick={clearFilters} className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 px-4 rounded-xl">مسح الفلاتر</button>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600 font-bold">النتائج: {filteredTransactions.length} من {transactions.length}</div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-5 py-3 text-right">النوع</th>
              <th className="px-5 py-3 text-right">المبلغ</th>
              <th className="px-5 py-3 text-right">التفاصيل</th>
              <th className="px-5 py-3 text-right">التاريخ</th>
              <th className="px-5 py-3 text-right">الإيصال/التصحيح</th>
            </tr>
          </thead>
          <tbody>{filteredTransactions.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}</tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">{filteredTransactions.map((tx) => <TransactionCard key={tx.id} tx={tx} />)}</div>

      {correctionTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-5 md:p-6 border border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-2xl font-black text-gray-900">{correctionMode === 'correct' ? '✏️ تعديل مبلغ العملية' : '🛑 إلغاء العملية'}</h2>
                <p className="text-sm text-gray-500 mt-1">{correctionTarget.reference_number || correctionTarget.id}</p>
              </div>
              <button type="button" onClick={closeCorrection} disabled={savingCorrection} className="bg-gray-100 hover:bg-gray-200 rounded-xl px-3 py-2 font-bold">×</button>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 mb-4 text-sm text-gray-800 space-y-1">
              <div><strong>النوع:</strong> {getTypeLabel(correctionTarget.type)}</div>
              <div><strong>المبلغ الحالي:</strong> {formatNumber(correctionTarget.amount)} د.ل</div>
              <div><strong>التفاصيل:</strong> {correctionTarget.description || '-'}</div>
            </div>

            {correctionMode === 'correct' && (
              <div className="mb-4">
                <label className="block text-sm font-bold text-gray-700 mb-2">المبلغ الصحيح</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={correctionAmount}
                  onChange={(e) => setCorrectionAmount(e.target.value)}
                  disabled={savingCorrection}
                  className="w-full border-2 border-gray-300 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input"
                />
                <p className="text-xs text-gray-500 mt-2">النظام سيحسب الفرق ويعدل رصيد المحل/المحفظة/المستخدم تلقائياً حسب نوع العملية.</p>
              </div>
            )}

            <div className="mb-5">
              <label className="block text-sm font-bold text-gray-700 mb-2">سبب التصحيح</label>
              <textarea
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                disabled={savingCorrection}
                placeholder="مثال: المبلغ كان 1350 والصحيح 1300 / عملية مكررة / خطأ إدخال"
                rows={3}
                className="w-full border-2 border-gray-300 rounded-xl p-3 focus:border-orange-500 focus:outline-none mobile-input"
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-3 text-sm text-yellow-900 mb-4">
              هذه العملية لا تُحذف من السجل؛ سيتم حفظها كعملية ملغية أو مصححة حتى تبقى المراجعة واضحة.
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button type="button" onClick={submitCorrection} disabled={savingCorrection} className="flex-1 bg-gray-900 hover:bg-black disabled:bg-gray-400 text-white font-bold py-3 rounded-xl">
                {savingCorrection ? 'جاري التنفيذ...' : correctionMode === 'correct' ? 'تأكيد تعديل المبلغ' : 'تأكيد الإلغاء'}
              </button>
              <button type="button" onClick={closeCorrection} disabled={savingCorrection} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-3 rounded-xl">رجوع</button>
            </div>
          </div>
        </div>
      )}

      {filteredTransactions.length === 0 && <div className="text-center text-gray-500 mt-8 text-lg">لا توجد عمليات مطابقة</div>}
    </div>
  );
}

export default Transactions;
